// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { IntegrationIcon } from '@/components/integration-icon';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  DriftEntityType,
  findDriftCapableIntegration,
  isDriftCapableNeedsReview,
} from '@/integrations/drift-support';
import { SFDCDriftEntityType } from '@/sfdc/api/types';
import { DriftResolutionPopover as SFDCDriftResolutionPopover } from '@/sfdc/components/drift-resolution-popover';
import { Integration, IntegrationId } from '@/utilities/integration';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { ReactNode, Ref, useCallback, useEffect, useState } from 'react';

interface Props {
  status: Integration | Integration[];
  width?: number | string;
  iconOpacity?: number;
  children?: ReactNode;
  /**
   * The Hardfin entity type backing this chip. When paired with
   * entityKey AND the integration list contains a drift-capable
   * platform, IntegrationStatus mounts the drift-resolution popover
   * inline. Omit for read-only contexts (audit logs, etc).
   */
  entityType?: DriftEntityType;
  /** Paired with entityType — the Hardfin entity key for popover wiring. */
  entityKey?: string;
  /** Display name surfaced in the pull-success toast. */
  entityDisplayName?: string;
  /** Fires after a successful Pull / Dismiss inside the popover. */
  onResolved?: () => void;
}

function SingleIntegrationIcon(props: {
  link: Integration;
  width: number | string;
  iconOpacity?: number;
  children?: ReactNode;
  onResolveClick?: (integrationId: IntegrationId) => void;
  resolveAnchorRef?: Ref<HTMLDivElement>;
}) {
  if (props.link.id === 'NONE') {
    return null;
  }

  const needsReview = isDriftCapableNeedsReview(props.link);
  const onResolveClick = props.onResolveClick;

  // Drift-capable icon: click opens the popover instead of the
  // external link. The popover shows discrepancies or the all-synced
  // placeholder, and surfaces a "View on <CRM>" link inside.
  if (onResolveClick) {
    const tooltipTitle = needsReview
      ? `This item is linked to your ${props.link.type} but data is out of sync (click to view status)`
      : `This item is linked to your ${props.link.type} and all data is in sync (click to view status)`;
    return (
      <Tooltip title={tooltipTitle} placement="bottom" arrow>
        <Box
          ref={props.resolveAnchorRef}
          sx={{ display: 'flex', alignItems: 'center', width: props.width }}
        >
          <IconButton
            size="small"
            onClick={() => onResolveClick(props.link.id)}
            sx={{ padding: 0 }}
            aria-label={`${props.link.label} sync details`}
          >
            <Box sx={{ display: 'flex', opacity: props.iconOpacity ?? 1 }}>
              <IntegrationIcon
                integrationId={props.link.id}
                width={props.width}
                linked={true}
                needsReview={needsReview}
              />
            </Box>
          </IconButton>
          {props.children}
        </Box>
      </Tooltip>
    );
  }

  const linkedTooltip = needsReview
    ? `${props.link.label} is out of sync`
    : `This item is linked to your ${props.link.type}`;
  const unlinkedTooltip = `This item is not linked to your ${props.link.type}`;

  if (props.link.link) {
    // Error state - link starts with #
    if (props.link.link.startsWith('#')) {
      return (
        <Tooltip title={props.link.link.substring(1)} placement="bottom" arrow>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: props.width,
              opacity: props.iconOpacity ?? 0.65,
            }}
          >
            <Box sx={{ display: 'flex', opacity: props.iconOpacity ?? 1 }}>
              <IntegrationIcon
                integrationId={props.link.id}
                width={props.width}
                linked={true}
                needsReview={needsReview}
              />
            </Box>
            {props.children}
          </Box>
        </Tooltip>
      );
    }

    // Linked state - clickable
    return (
      <Tooltip title={linkedTooltip} placement="bottom" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', width: props.width }}>
          <a
            href={props.link.link}
            target="_blank"
            rel="noreferrer"
            style={{ lineHeight: 0 }}
          >
            <Box sx={{ display: 'flex', opacity: props.iconOpacity ?? 1 }}>
              <IntegrationIcon
                integrationId={props.link.id}
                width={props.width}
                linked={true}
                needsReview={needsReview}
              />
            </Box>
            {props.children}
          </a>
        </Box>
      </Tooltip>
    );
  }

  // Unlinked state
  return (
    <Tooltip title={unlinkedTooltip} placement="bottom" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', width: props.width }}>
        <Box sx={{ display: 'flex', opacity: props.iconOpacity ?? 0.5 }}>
          <IntegrationIcon
            integrationId={props.link.id}
            width={props.width}
            linked={false}
            needsReview={needsReview}
          />
        </Box>
        {props.children}
      </Box>
    </Tooltip>
  );
}

export function IntegrationStatus(props: Props) {
  const integrations = Array.isArray(props.status)
    ? props.status
    : [props.status];

  const {
    isOpen: isDriftOpen,
    open: openDrift,
    close: closeDrift,
  } = useDisclosure();
  // State-based ref so React notifies us when the anchor unmounts —
  // e.g., after a Pull on Name causes the listing to refetch and the
  // row falls off the page. Close the popover in that case instead
  // of leaving it floating without an anchor.
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const setAnchorRef = useCallback((node: HTMLDivElement | null) => {
    setAnchorEl(node);
  }, []);
  useEffect(() => {
    if (anchorEl === null && isDriftOpen) {
      closeDrift();
    }
  }, [anchorEl, isDriftOpen, closeDrift]);
  // Also close when the anchor scrolls / moves out of viewport (e.g.,
  // sort-order change keeps the row mounted but pushes it off-screen).
  useEffect(() => {
    if (!anchorEl || !isDriftOpen) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        closeDrift();
      }
    });
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [anchorEl, isDriftOpen, closeDrift]);

  const driftIntegration =
    props.entityType !== undefined && props.entityKey !== undefined
      ? findDriftCapableIntegration(integrations)
      : undefined;

  // Only the drift-capable icon attaches the click handler + anchor
  // ref; other icons keep their external-link behavior.
  const driftPropsFor = (link: Integration) =>
    driftIntegration && link.id === driftIntegration.id
      ? { onResolveClick: openDrift, resolveAnchorRef: setAnchorRef }
      : {};

  const validLinks = integrations.filter((link) => link.id !== 'NONE');

  const icons = (() => {
    if (validLinks.length === 0) {
      return null;
    }
    if (validLinks.length === 1) {
      const link = validLinks[0];
      return (
        <SingleIntegrationIcon
          link={link}
          width={props.width ?? '24px'}
          iconOpacity={props.iconOpacity}
          {...driftPropsFor(link)}
        >
          {props.children}
        </SingleIntegrationIcon>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {validLinks.map((link) => (
          <SingleIntegrationIcon
            key={link.id}
            link={link}
            width={props.width ?? '24px'}
            iconOpacity={props.iconOpacity}
            {...driftPropsFor(link)}
          />
        ))}
        {props.children}
      </Stack>
    );
  })();

  return (
    <>
      {icons}
      {driftIntegration && props.entityType && props.entityKey && (
        <SFDCDriftResolutionPopover
          anchorEl={anchorEl}
          open={isDriftOpen && anchorEl !== null}
          onClose={closeDrift}
          entityType={props.entityType as SFDCDriftEntityType}
          entityKey={props.entityKey}
          entityDisplayName={props.entityDisplayName}
          externalUrl={driftIntegration.link}
          externalLabel={driftIntegration.label}
          onResolved={props.onResolved}
        />
      )}
    </>
  );
}
